import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper, Megaphone, Sparkles, CalendarDays } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { API_BASE, BACKEND_URL } from "../lib/site";
import { useContent } from "../lib/content";

const TYPE_META = {
    inauguracao: {
        label: "Inauguração",
        icon: PartyPopper,
        cls: "bg-[#F57A3A] text-[#F7F4EC]",
    },
    promocao: {
        label: "Promoção",
        icon: Megaphone,
        cls: "bg-[#F1C75B] text-[#1A231E]",
    },
    evento: {
        label: "Evento",
        icon: Sparkles,
        cls: "bg-[#0E6B3F] text-[#F7F4EC]",
    },
};

const fmtDate = (d) =>
    d
        ? new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
          })
        : null;

export default function Novidades() {
    const [posts, setPosts] = useState(null);
    const t = useContent().novidades;

    useEffect(() => {
        fetch(`${API_BASE}/posts`)
            .then((r) => r.json())
            .then((d) => setPosts(Array.isArray(d) ? d : []))
            .catch(() => setPosts([]));
    }, []);

    return (
        <section id="novidades" className="scroll-mt-24 bg-[#F7F4EC] py-20 sm:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal className="max-w-2xl">
                    <SectionTag tone="orange">Novidades Conbox</SectionTag>
                    <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#1A231E]">
                        {t.title}
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-[#1A231E]/70 leading-relaxed">
                        {t.subtitle}
                    </p>
                </Reveal>

                {posts === null ? (
                    <div className="mt-10 h-40 rounded-3xl bg-[#EFEAE0] animate-pulse" />
                ) : posts.length === 0 ? (
                    <div
                        data-testid="novidades-empty"
                        className="mt-10 rounded-[1.75rem] border-2 border-dashed border-[#1A231E]/20 bg-[#EFEAE0]/60 px-8 py-12 text-center"
                    >
                        <Sparkles className="w-8 h-8 text-[#F57A3A] mx-auto" />
                        <p className="mt-4 text-lg font-extrabold text-[#1A231E]">
                            Em breve, novidades por aqui.
                        </p>
                        <p className="mt-2 text-sm text-[#1A231E]/60 font-semibold">
                            Inaugurações, promoções e eventos dos condomínios Conbox aparecerão
                            nesta seção.
                        </p>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="novidades-list">
                        <AnimatePresence>
                            {posts.map((p, i) => {
                                const meta = TYPE_META[p.type] || TYPE_META.evento;
                                const date = fmtDate(p.event_date);
                                return (
                                    <motion.article
                                        key={p.id}
                                        initial={{ opacity: 0, y: 24 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.15 }}
                                        transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                                        data-testid="novidades-item"
                                        className="group rounded-[1.75rem] overflow-hidden bg-white border-2 border-[#1A231E]/12 sticker-soft hover:sticker transition-shadow duration-300 flex flex-col"
                                    >
                                        {p.image_path && (
                                            <div className="h-48 overflow-hidden">
                                                <img
                                                    src={`${BACKEND_URL}/api/media/${p.image_path}`}
                                                    alt={p.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                />
                                            </div>
                                        )}
                                        <div className="p-6 flex flex-col flex-1">
                                            <div className="flex items-center justify-between gap-3">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full ${meta.cls}`}
                                                >
                                                    <meta.icon className="w-3.5 h-3.5" />
                                                    {meta.label}
                                                </span>
                                                {date && (
                                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1A231E]/50">
                                                        <CalendarDays className="w-4 h-4" />
                                                        {date}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="mt-4 text-xl font-extrabold text-[#1A231E]">
                                                {p.title}
                                            </h3>
                                            <p className="mt-2 text-sm text-[#1A231E]/70 leading-relaxed flex-1">
                                                {p.description}
                                            </p>
                                        </div>
                                    </motion.article>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </section>
    );
}
